import { Fragment, useRef } from "react";
import { Dialog, Transition } from "@headlessui/react";
import type { ReactNode, MutableRefObject } from "react";

import CloseIcon from "@assets/icons/close.svg";
import "./Dialog.css";

import useDialogStore from "@store/useDialogStore";
import { CallToActionConfig } from "@app-types/common.type";
import { ANIMATION_DURATION_IN_MS } from "@config/constants";
import { cn } from "@utils/tailwind.util";
import { Button } from "@ui/Button";
import { View } from "@ui/View";

export interface BaseDialogProps {
  title?: ReactNode;
  children: ReactNode;
  showCloseButton?: boolean;
  showHeader?: boolean;
  initialFocus?: MutableRefObject<HTMLElement | null>;
  HeaderPrefixIcon?: ReactNode;
  HeaderSuffixIcon?: ReactNode;
  primaryButton?: CallToActionConfig;
  secondaryButton?: CallToActionConfig;
  tertiaryButton?: CallToActionConfig;
  className?: string;
  bodyClassName?: string;
  titleClassName?: string;
  position?: "CENTER" | "TOP";
  isFullScreen?: boolean;
}

export function BaseDialog({
  title,
  children,
  showCloseButton = true,
  showHeader = true,
  initialFocus,
  HeaderPrefixIcon,
  HeaderSuffixIcon,
  primaryButton,
  secondaryButton,
  tertiaryButton,
  className,
  bodyClassName,
  titleClassName = "text-xl",
  position = "CENTER",
  isFullScreen = false,
}: BaseDialogProps) {
  const { closeDialog } = useDialogStore();
  const dialogRef = useRef<HTMLDivElement>(null);
  const fallbackFocusRef = useRef(null);

  if (!initialFocus) {
    initialFocus = fallbackFocusRef;
  }

  const defaultFocusRef = useRef(null);
  // If no initial focus was specified, focus on the header. This will prevent the close button from having the focus.
  if (initialFocus === undefined) {
    initialFocus = defaultFocusRef;
  }

  const positionClassName = position === "TOP" ? "items-start" : "items-center";
  const paddingClassName = isFullScreen ? "" : "p-4";

  return (
    <div
      className={cn(
        "flex min-h-full justify-center text-center",
        paddingClassName,
        positionClassName
      )}
    >
      <Transition.Child
        as={Fragment}
        enter={`ease-out duration-${ANIMATION_DURATION_IN_MS}`}
        enterFrom="opacity-0 scale-90"
        enterTo="opacity-100 scale-100"
        leave={`ease-in duration-${ANIMATION_DURATION_IN_MS}`}
        leaveFrom="opacity-100 scale-100"
        leaveTo="opacity-0 scale-90"
      >
        <Dialog.Panel
          className={cn(
            "relative transform overflow-hidden w-full text-left align-middle shadow-xl",
            isFullScreen
              ? "max-w-none h-full rounded-none p-0 bg-white dark:bg-gray-800"
              : "max-w-[400px] rounded-lg p-0 bg-white dark:bg-gray-800"
          )}
        >
          <div
            ref={dialogRef}
            className={cn("w-full flex flex-col", isFullScreen ? "h-full" : "min-w-0", className)}
          >
            <View.Condition if={showHeader}>
              <Dialog.Title as="div" className="flex flex-col items-center p-4">
                <div className={cn("flex w-full items-center justify-between", titleClassName)}>
                  <h2
                    className="flex flex-row items-center gap-2 align-middle dark:text-invertedPrimary80 font-semibold leading-6"
                    ref={defaultFocusRef}
                  >
                    {HeaderPrefixIcon && HeaderPrefixIcon}
                    {title}
                    {HeaderSuffixIcon && HeaderSuffixIcon}
                  </h2>

                  {showCloseButton && (
                    <Button
                      onClick={closeDialog}
                      buttonType={"SECONDARY"}
                      className={cn(
                        "flex-shring-0 h-auto !p-1 text-content-secondary hover:text-gray-700",
                        "dark:bg-gray-800 dark:hover:bg-gray-700 dark:hover:border dark:hover:border-gray-600 dark:text-invertedPrimary80"
                      )}
                    >
                      <span className="sr-only">Close</span>
                      <CloseIcon width={20} height={20} className="dark:text-white" />
                    </Button>
                  )}
                </div>
              </Dialog.Title>
            </View.Condition>

            <div
              className={cn(
                "hover-scrollbar flex flex-grow flex-col",
                isFullScreen ? "min-h-[calc(100vh_-_174px)]" : "max-h-[70vh]",
                bodyClassName
              )}
            >
              {children}
            </div>

            {(primaryButton || secondaryButton || tertiaryButton) && (
              <div className="flex justify-between p-4">
                <div className="w-full flex flex-col gap-2">
                  {primaryButton && (
                    <Button
                      key={primaryButton.id ?? "primary-button"}
                      className={primaryButton.additionalClasses}
                      disabled={primaryButton.disabled ?? false}
                      isDisabled={primaryButton.disabled ?? false}
                      onClick={primaryButton.callback}
                      buttonType={primaryButton.buttonTypeOverride ?? "PRIMARY"}
                      type={primaryButton.type ?? "button"}
                      PrefixIcon={primaryButton.PrefixIcon}
                      SuffixIcon={primaryButton.SuffixIcon}
                    >
                      {primaryButton.label}
                    </Button>
                  )}

                  {secondaryButton && (
                    <Button
                      key={secondaryButton.id ?? "secondary-button"}
                      className={cn(
                        "text-gray-800 dark:text-invertedPrimary80 dark:bg-gray-700 dark:hover:bg-gray-600 border-gray-325 disabled:bg-white80 disabled:opacity-50 dark:disabled:hover:bg-gray-700",
                        secondaryButton.additionalClasses
                      )}
                      disabled={secondaryButton.disabled ?? false}
                      isDisabled={secondaryButton.disabled ?? false}
                      onClick={secondaryButton.callback}
                      buttonType={secondaryButton.buttonTypeOverride ?? "SECONDARY"}
                      type={secondaryButton.type ?? "button"}
                      PrefixIcon={secondaryButton.PrefixIcon}
                      SuffixIcon={secondaryButton.SuffixIcon}
                    >
                      {secondaryButton.label}
                    </Button>
                  )}

                  {tertiaryButton && (
                    <Button
                      key={tertiaryButton.id ?? "tertiary-button"}
                      className={tertiaryButton.additionalClasses}
                      isDisabled={tertiaryButton.disabled ?? false}
                      onClick={tertiaryButton.callback}
                      buttonType={tertiaryButton.buttonTypeOverride ?? "SECONDARY"}
                      type={tertiaryButton.type ?? "button"}
                      PrefixIcon={tertiaryButton.PrefixIcon}
                      SuffixIcon={tertiaryButton.SuffixIcon}
                    >
                      {tertiaryButton.label}
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        </Dialog.Panel>
      </Transition.Child>
    </div>
  );
}
